// src/components/onboarding/SpotlightTour.jsx
import React, { useState, useEffect } from 'react';
import '../styles/SpotlightTour.scss';

const SpotlightTour = ({ 
  steps, 
  tourKey = 'dashboard_tour_completed',
  onComplete = () => {},
  onSkip = () => {},
  enabled = true 
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [showTour, setShowTour] = useState(false);
  const [targetElement, setTargetElement] = useState(null);
  const [targetRect, setTargetRect] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({});
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);

  // Check tour status when enabled changes
  useEffect(() => {
    if (enabled) {
      const completed = localStorage.getItem(tourKey);
      console.log('Tour enabled, completed status:', completed);
      if (completed !== 'true') {
        setShowTour(true);
        setCurrentStep(0);
        setRetryCount(0);
        setTimeout(() => highlightStep(0), 1000);
      } else {
        setIsLoading(false);
      }
    } else {
      setShowTour(false);
      clearHighlight();
    }
  }, [enabled]);

  const highlightStep = (stepIndex) => {
    const step = steps[stepIndex];
    if (!step) {
      console.error('Step not found:', stepIndex);
      return;
    }

    // Clear previous highlight
    if (targetElement) {
      targetElement.classList.remove('tour-highlight-element');
    }

    // Find the target element
    console.log(`Step ${stepIndex + 1}/${steps.length}: Looking for "${step.selector}"`);
    const element = document.querySelector(step.selector);
    console.log(`Element found:`, element ? '✅ YES' : '❌ NO');

    if (element) {
      // Reset retry count on success
      setRetryCount(0);
      
      // Check if element is visible
      const rect = element.getBoundingClientRect();
      const isElementVisible = rect.width > 0 && rect.height > 0;
      console.log(`Element visible:`, isElementVisible ? '✅ YES' : '❌ NO', rect);
      
      if (!isElementVisible) {
        console.warn(`Element "${step.selector}" is not visible, retrying...`);
        setTimeout(() => {
          highlightStep(stepIndex);
        }, 500);
        return;
      }

      // Add highlight class
      element.classList.add('tour-highlight-element');
      setTargetElement(element);
      
      // Scroll to element with smooth behavior
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      
      // Calculate if element is in viewport
      const isInViewport = rect.top >= 0 && rect.bottom <= viewportHeight;
      
      // If element is not fully in viewport or is in the bottom half, scroll to it
      if (!isInViewport || rect.top > viewportHeight * 0.25) {
        // Check if this is a card/section at the bottom (steps 10, 11, 12)
        const isBottomSection = step.selector.includes('projects-showcase') || 
                               step.selector.includes('updates-card') || 
                               step.selector.includes('proposals-card');
        
        if (isBottomSection) {
          // For bottom sections, scroll to the top of the element
          element.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start',
          });
        } else {
          element.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
          });
        }
        
        // Wait for scroll to complete before showing tooltip
        setTimeout(() => {
          // Recalculate position after scroll
          const newRect = element.getBoundingClientRect();
          setTargetRect(newRect);
          calculateTooltipPosition(newRect, viewportWidth, viewportHeight);
          setIsVisible(true);
          setIsLoading(false);
        }, 700);
      } else {
        setTargetRect(rect);
        calculateTooltipPosition(rect, viewportWidth, viewportHeight);
        setIsVisible(true);
        setIsLoading(false);
      }
      
      console.log(`Step ${stepIndex + 1} highlighted successfully!`);
    } else {
      // Element not found, retry with backoff
      if (retryCount < 10) {
        const delay = Math.min(1000 + (retryCount * 500), 5000);
        console.warn(`Element "${step.selector}" not found, retrying in ${delay}ms (attempt ${retryCount + 1}/10)`);
        setRetryCount(prev => prev + 1);
        setTimeout(() => {
          highlightStep(stepIndex);
        }, delay);
      } else {
        console.error(`Failed to find "${step.selector}" after 10 attempts, skipping to next step`);
        handleNext();
      }
    }
  };

  const calculateTooltipPosition = (rect, viewportWidth, viewportHeight) => {
    const tooltipWidth = Math.min(380, viewportWidth - 40);
    const tooltipHeight = 250;
    const padding = 20;
    const arrowSize = 16;

    // Calculate available space above and below
    const spaceAbove = rect.top - padding;
    const spaceBelow = viewportHeight - rect.bottom - padding;

    let top, arrowTop, isAbove;

    // Check if this is a bottom section (projects-showcase, updates-card, proposals-card)
    const isBottomSection = targetElement?.classList.contains('projects-showcase') ||
                           targetElement?.classList.contains('updates-card') ||
                           targetElement?.classList.contains('proposals-card');

    if (isBottomSection) {
      // For bottom sections, always position above if possible
      if (spaceAbove >= tooltipHeight + arrowSize) {
        top = rect.top - tooltipHeight - arrowSize - padding;
        arrowTop = tooltipHeight;
        isAbove = true;
      } else {
        // If not enough space above, position at bottom of viewport
        top = viewportHeight - tooltipHeight - padding - 20;
        arrowTop = -arrowSize;
        isAbove = false;
        // Adjust to ensure tooltip is fully visible
        if (top < padding) {
          top = padding + 10;
        }
      }
    } else {
      // Normal positioning logic
      if (spaceBelow >= tooltipHeight + arrowSize) {
        // Position below
        top = rect.bottom + arrowSize + padding;
        arrowTop = -arrowSize;
        isAbove = false;
      } else if (spaceAbove >= tooltipHeight + arrowSize) {
        // Position above
        top = rect.top - tooltipHeight - arrowSize - padding;
        arrowTop = tooltipHeight;
        isAbove = true;
      } else {
        // Not enough space either way - position at bottom
        top = Math.max(padding, viewportHeight - tooltipHeight - padding - 20);
        arrowTop = -arrowSize;
        isAbove = false;
      }
    }

    // Ensure tooltip stays within viewport vertically
    if (top < padding) {
      top = padding + 10;
    }
    if (top + tooltipHeight > viewportHeight - padding) {
      top = viewportHeight - tooltipHeight - padding - 10;
    }

    // Calculate horizontal position
    let left = rect.left + rect.width / 2 - tooltipWidth / 2;
    
    // Ensure tooltip stays within viewport horizontally
    if (left < padding) {
      left = padding;
    } else if (left + tooltipWidth > viewportWidth - padding) {
      left = viewportWidth - tooltipWidth - padding;
    }

    // Calculate arrow position
    const targetCenter = rect.left + rect.width / 2;
    const tooltipCenter = left + tooltipWidth / 2;
    let arrowLeft = tooltipWidth / 2 - (tooltipCenter - targetCenter);

    // Clamp arrow
    if (arrowLeft < 20) arrowLeft = 20;
    if (arrowLeft > tooltipWidth - 20) arrowLeft = tooltipWidth - 20;

    console.log('Tooltip position:', { top, left, tooltipWidth, tooltipHeight, isAbove, isBottomSection });

    setTooltipPosition({
      top,
      left,
      width: tooltipWidth,
      arrowLeft,
      arrowTop,
      isAbove,
    });
  };

  const handleNext = () => {
    console.log('Next clicked, current step:', currentStep + 1, 'of', steps.length);
    
    if (currentStep < steps.length - 1) {
      // Clear current highlight
      clearHighlight();
      
      // Move to next step
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      setIsVisible(false);
      setIsLoading(true);
      setRetryCount(0);
      
      // Check if next step is a navbar item
      const nextStepData = steps[nextStep];
      if (nextStepData) {
        const isNavItem = nextStepData.selector.includes('tour-dashboard') || 
                         nextStepData.selector.includes('tour-projects') || 
                         nextStepData.selector.includes('tour-progress') || 
                         nextStepData.selector.includes('tour-messages') || 
                         nextStepData.selector.includes('tour-payments') || 
                         nextStepData.selector.includes('tour-mode-switch') || 
                         nextStepData.selector.includes('tour-help-desk');
        
        if (isNavItem) {
          window.scrollTo({ top: 0, behavior: 'smooth' });
          setTimeout(() => {
            highlightStep(nextStep);
          }, 600);
          return;
        }
      }
      
      setTimeout(() => {
        highlightStep(nextStep);
      }, 500);
    } else {
      // Last step - complete tour
      handleComplete();
    }
  };

  const handleSkip = () => {
    console.log('Tour skipped');
    clearHighlight();
    setShowTour(false);
    localStorage.setItem(tourKey, 'true');
    if (onSkip) onSkip();
    if (onComplete) onComplete();
  };

  const handleComplete = () => {
    console.log('Tour completed');
    clearHighlight();
    setShowTour(false);
    localStorage.setItem(tourKey, 'true');
    if (onComplete) onComplete();
  };

  const clearHighlight = () => {
    if (targetElement) {
      targetElement.classList.remove('tour-highlight-element');
    }
    setTargetElement(null);
    setTargetRect(null);
  };

  if (isLoading || !showTour || !targetRect || !isVisible) {
    return null;
  }

  const step = steps[currentStep];
  const isLast = currentStep === steps.length - 1;

  return (
    <div className="spotlight-tour-overlay">
      <div className="tour-overlay">
        {/* Highlight area with hole punch */}
        <div 
          className="tour-highlight"
          style={{
            top: targetRect.top - 8,
            left: targetRect.left - 8,
            width: targetRect.width + 16,
            height: targetRect.height + 16,
            boxShadow: `0 0 0 9999px rgba(0, 0, 0, 0.65)`,
          }}
        >
          <div className="tour-highlight-border" />
        </div>

        {/* Tooltip */}
        <div 
          className="tour-tooltip"
          style={{
            position: 'fixed',
            top: tooltipPosition.top,
            left: tooltipPosition.left,
            width: tooltipPosition.width,
            zIndex: 10001,
            maxHeight: '70vh',
            overflowY: 'auto',
          }}
        >
          {/* Arrow */}
          <div 
            className={`tour-tooltip-arrow ${tooltipPosition.isAbove ? 'arrow-down' : 'arrow-up'}`}
            style={{
              left: tooltipPosition.arrowLeft,
              top: tooltipPosition.arrowTop,
            }}
          />

          <div className="tour-tooltip-content">
            <div className="tour-tooltip-header">
              <div className="tour-icon-wrapper">
                <span className="tour-icon">{step.icon}</span>
              </div>
              <span className="tour-step-counter">
                {currentStep + 1} / {steps.length}
              </span>
            </div>
            
            <h3 className="tour-title">{step.title}</h3>
            <p className="tour-description">{step.description}</p>
            
            <div className="tour-actions">
              <button className="tour-skip-btn" onClick={handleSkip}>
                Skip Tour
              </button>
              <button className="tour-next-btn" onClick={handleNext}>
                {isLast ? 'Got it!' : 'Next →'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Debug info */}
      {/* {process.env.NODE_ENV === 'development' && (
        <div style={{
          position: 'fixed',
          bottom: 80,
          right: 20,
          zIndex: 10002,
          background: 'rgba(0,0,0,0.85)',
          color: '#00ff00',
          padding: '8px 14px',
          borderRadius: '6px',
          fontSize: '12px',
          fontFamily: 'monospace',
          pointerEvents: 'none',
          maxWidth: '300px',
          wordBreak: 'break-all',
        }}>
          Step {currentStep + 1}/{steps.length}: {step.title}
          <br />
          Selector: {step.selector}
          <br />
          Tooltip Top: {tooltipPosition.top}px
          <br />
          Tooltip Above: {tooltipPosition.isAbove ? '✅' : '❌'}
        </div>
      )} */}
    </div>
  );
};

export default SpotlightTour;